import { type FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import ReactDataSheet from 'react-datasheet';
import 'react-datasheet/lib/react-datasheet.css';

import { updateElement } from '@editor2/reducers/editor/editorSlice';
import { type GeoMap } from '@editor2/types/pageElements';

import GroupSelect from './GroupSelect';
import ColumnHeader from './ColumnHeader';

import { mapDataStyle } from './styles';
import { colorsToOptions, getKeyColumnName } from '@editor2/services/geoMapService';
import { type GridElement } from '@editor2/types/gridElement';

class MyReactDataSheet extends ReactDataSheet<GridElement, any> {}

interface Props {
	element: GeoMap;
	currentPageData: any[];
	courseId: number;
	currentPage: number;
	limit: number;
}

const DataTable: FC<Props> = (props) => {
	const { element, courseId, currentPageData, currentPage, limit } = props;

	const [grid, setGrid] = useState([]);
	const dispatch = useDispatch();

	const commonPayload = { elementId: element.family_id, courseId };

	const onContextMenu = (e, cell, i, j) => (cell.readOnly ? e.preventDefault() : null);

	const onCellsChanged = (changes) => {
		let updatedLabels = [...element.labels];
		let updatedData = { ...element.data };

		changes.forEach(({ row, col, value }) => {
			if (row === 0) {
				updatedLabels = [...updatedLabels.slice(0, col - 1), value, ...updatedLabels.slice(col)];
			} else {
				const featureName = currentPageData[row - 1].name;
				const featureData = updatedData[featureName] || element.labels.map(() => '');

				updatedData = {
					...updatedData,
					[featureName]: [...featureData.slice(0, col - 1), value, ...featureData.slice(col)]
				};
			}
		});

		dispatch(updateElement({ ...commonPayload, data: updatedData, labels: updatedLabels }));
	};

	const onSelectCellChange = ({ row, col, value }) => {
		const featureName = currentPageData[row - 1].name;
		const featureData = element.data[featureName] || element.labels.map(() => '');

		const updatedData = {
			...element.data,
			[featureName]: [...featureData.slice(0, col - 1), value, ...featureData.slice(col)]
		};

		dispatch(updateElement({ ...commonPayload, data: updatedData }));
	};

	const onAddColumnClick = (index: number) => {
		const updatedData = Object.keys(element.data).reduce((acc, curr) => {
			return {
				...acc,
				[curr]: [...element.data[curr].slice(0, index), '', ...element.data[curr].slice(index)]
			};
		}, {});

		const updatedLabels = [
			...element.labels.slice(0, index),
			'Column',
			...element.labels.slice(index)
		];

		dispatch(updateElement({ ...commonPayload, data: updatedData, labels: updatedLabels }));
	};

	const onDeleteColumnClick = (index: number) => {
		const updatedData = Object.keys(element.data).reduce((acc, curr) => {
			return {
				...acc,
				[curr]: [...element.data[curr].slice(0, index), ...element.data[curr].slice(index + 1)]
			};
		}, {});

		const updatedLabels = [...element.labels.slice(0, index), ...element.labels.slice(index + 1)];

		dispatch(updateElement({ ...commonPayload, data: updatedData, labels: updatedLabels }));
	};

	const generateHeaderRow = () => {
		const columns = element.labels.map((label, index) => ({
			...(label === 'Group' && { readOnly: true }),
			value: (
				<ColumnHeader
					label={label as string}
					index={index}
					readOnly={label === 'Group'}
					onAddColumnClick={onAddColumnClick}
					onDeleteColumnClick={onDeleteColumnClick}
				/>
			),
			expr: label
		}));

		return [{ readOnly: true, value: getKeyColumnName(element.geomap_type) }, ...columns];
	};

	const getNotGroupCells = (key) => {
		return element.data[key]
			? element.data[key].slice(1).map((cellData) => ({ value: cellData }))
			: element.labels.slice(1).map(() => ({ value: '' }));
	};

	const generateRow = ({ key, options, index }) => {
		const featureData = element.data[key];
		const optionId = featureData ? featureData[0] : '';
		const selectedValue = element.colors.find((colorRow) => colorRow.id === optionId);

		const row = [
			{ value: key, readOnly: true },
			{
				value: optionId,
				component: (
					<GroupSelect
						options={options}
						value={selectedValue}
						onSelectCellChange={onSelectCellChange}
						row={index + 1}
						col={1}
					/>
				),
				forceComponent: true
			},
			...getNotGroupCells(key)
		];

		return row;
	};

	const generateGrid = () => {
		const headerRow = generateHeaderRow();
		const options = colorsToOptions(element.colors);

		const rows = currentPageData.map((item, index) =>
			generateRow({ key: item.name, options, index })
		);

		return [headerRow, ...rows];
	};

	useEffect(() => {
		const rows = generateGrid();
		setGrid(rows);

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentPageData, limit, element.data, element.labels, element.colors]);

	return (
		<MyReactDataSheet
			className={mapDataStyle}
			data={grid}
			valueRenderer={(cell) => cell.value}
			dataRenderer={(cell) => cell.expr || cell.value}
			onContextMenu={onContextMenu}
			onCellsChanged={onCellsChanged}
		/>
	);
};

export default DataTable;
